import type { IcePathTelemetry } from "./icePath";
import { abortError, setupDelay } from "./parallelSetup";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://dialer1.bcall.us";

export interface CallCredentials {
  user: string;
  authorizationUsername?: string;
  pass: string;
  targetSipUser: string;
  domain: string;
  websocket: string;
  iceServers: RTCIceServer[];
  sessionId?: string;
  sessionToken?: string;
  apiBaseUrl: string;
  lateReceiverJoin?: boolean;
}

export type CallSessionErrorCode = "RECIPIENT_UNAVAILABLE" | "SESSION_EXPIRED" | "NO_ANSWER";

export class CallSessionError extends Error {
  constructor(public readonly code: CallSessionErrorCode) {
    super(code);
    this.name = "CallSessionError";
  }
}

interface V2CallSessionResponse {
  capabilities?: { lateReceiverJoin?: boolean };
  schemaVersion?: number;
  sessionId: string;
  sessionToken: string;
  sip?: {
    username: string;
    authorizationUsername?: string;
    password: string;
    aorUser?: string;
    aor?: string;
    targetAor: string;
    domain: string;
    websocket: string;
  };
  iceServers?: RTCIceServer[];
  userpass?: string;
  sipAorUser?: string;
  authorizationUsername?: string;
  password?: string;
  targetSipUser?: string;
  domain?: string;
  websocket?: string;
  turn?: { username: string; password: string };
}

export interface CallSessionStatus {
  status?: string;
  targetReceiverCount?: number;
  readyReceiverCount?: number;
  fanoutWaitUntil?: string | null;
}

export function fanoutReady(status: CallSessionStatus, now = Date.now()): boolean {
  if (status.status !== "ready" && status.status !== "ringing") return false;
  if (!Number.isFinite(status.targetReceiverCount) || !Number.isFinite(status.readyReceiverCount)) return true;
  const targetCount = Math.max(1, Number(status.targetReceiverCount));
  const readyCount = Math.max(0, Number(status.readyReceiverCount));
  if (readyCount >= targetCount) return true;
  const fanoutWaitUntil = status.fanoutWaitUntil ? Date.parse(status.fanoutWaitUntil) : NaN;
  return !Number.isFinite(fanoutWaitUntil) || fanoutWaitUntil <= now;
}

function userFromAor(aor: string): string {
  const match = /^sip:(.+)@[^@]+$/i.exec(aor);
  if (!match) throw new Error("Invalid SIP target returned by the server");
  return match[1];
}

function aorUserFromAuthorization(username: string): string {
  const separator = username.indexOf(":");
  return separator >= 0 ? username.slice(separator + 1) : username;
}

export function normalizeCallSession(data: V2CallSessionResponse): CallCredentials {
  const sip = data.sip;
  const authorizationUsername = sip?.authorizationUsername || sip?.username || data.authorizationUsername || data.userpass;
  const user = sip?.aorUser || (sip?.aor ? userFromAor(sip.aor) : data.sipAorUser) ||
    (authorizationUsername ? aorUserFromAuthorization(authorizationUsername) : undefined);
  const pass = sip?.password || data.password;
  const rawTargetSipUser = sip?.targetAor ? userFromAor(sip.targetAor) : data.targetSipUser;
  const targetSipUser = rawTargetSipUser ? aorUserFromAuthorization(rawTargetSipUser) : undefined;
  const domain = sip?.domain || data.domain;
  const websocket = sip?.websocket || data.websocket;
  const iceServers = data.iceServers || (data.turn ? [
    { urls: ["stun:turn.bcall.us:3478"] },
    {
      urls: [
        "turn:turn.bcall.us:3478?transport=udp",
        "turn:turn.bcall.us:3478?transport=tcp",
        "turns:turn.bcall.us:5349?transport=tcp"
      ],
      username: data.turn.username,
      credential: data.turn.password
    }
  ] : []);

  if (!user || !authorizationUsername || !pass || !targetSipUser || !domain || !websocket || !iceServers.length) {
    throw new Error("Incomplete RTC configuration returned by the server");
  }
  return {
    user,
    authorizationUsername,
    pass,
    targetSipUser,
    domain,
    websocket,
    iceServers,
    sessionId: data.sessionId,
    sessionToken: data.sessionToken,
    lateReceiverJoin: data.capabilities?.lateReceiverJoin === true,
    apiBaseUrl: API_BASE_URL
  };
}

export function storeCallCredentials(credentials: CallCredentials): void {
  sessionStorage.setItem("rtcCallSession", JSON.stringify(credentials));
}

export function clearCallCredentials(): void {
  sessionStorage.removeItem("rtcCallSession");
  sessionStorage.removeItem("userpass");
  sessionStorage.removeItem("password");
  sessionStorage.removeItem("numberadv");
  sessionStorage.removeItem("sessionId");
  sessionStorage.removeItem("sessionToken");
  sessionStorage.removeItem("turnuser");
  sessionStorage.removeItem("turnpassword");
}

export function readCallCredentials(): CallCredentials {
  const serialized = sessionStorage.getItem("rtcCallSession");
  if (serialized) return JSON.parse(serialized) as CallCredentials;

  const user = sessionStorage.getItem("userpass");
  const pass = sessionStorage.getItem("password");
  const targetSipUser = sessionStorage.getItem("numberadv");
  const domain = sessionStorage.getItem("sipDomain");
  const websocket = sessionStorage.getItem("sipServer");
  const turnUser = sessionStorage.getItem("turnuser");
  const turnPass = sessionStorage.getItem("turnpassword");
  if (!user || !pass || !targetSipUser || !domain || !websocket || !turnUser || !turnPass) {
    throw new Error("Missing call-session configuration");
  }
  return {
    user,
    authorizationUsername: user,
    pass,
    targetSipUser,
    domain,
    websocket,
    sessionId: sessionStorage.getItem("sessionId") || undefined,
    sessionToken: sessionStorage.getItem("sessionToken") || undefined,
    apiBaseUrl: API_BASE_URL,
    iceServers: [
      { urls: ["stun:turn.bcall.us:3478"] },
      {
        urls: [
          "turn:turn.bcall.us:3478?transport=udp",
          "turn:turn.bcall.us:3478?transport=tcp",
          "turns:turn.bcall.us:5349?transport=tcp"
        ],
        username: turnUser,
        credential: turnPass
      }
    ]
  };
}

export async function waitUntilReady(credentials: CallCredentials, shouldStop?: () => boolean, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) throw abortError();
  if (shouldStop?.()) return;
  // This capability is advertised only by servers with pending INVITE storage and
  // late contact insertion. Older deployments retain the existing fanout wait.
  if (credentials.lateReceiverJoin === true) return;
  if (!credentials.sessionId || !credentials.sessionToken) return;
  const deadline = Date.now() + 30_000;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw abortError();
    if (shouldStop?.()) return;
    const response = await fetch(`${credentials.apiBaseUrl}/call-sessions/${encodeURIComponent(credentials.sessionId)}/status`, {
      headers: { authorization: `Bearer ${credentials.sessionToken}` },
      signal
    });
    if (shouldStop?.()) return;
    let nextPollMs = 750;
    if (response.ok) {
      const result = await response.json() as CallSessionStatus;
      if (fanoutReady(result)) return;
      if (result.status === "failed" || result.status === "ended") throw new CallSessionError("RECIPIENT_UNAVAILABLE");
      const settleAt = result.fanoutWaitUntil ? Date.parse(result.fanoutWaitUntil) : NaN;
      if (Number.isFinite(settleAt) && settleAt > Date.now()) nextPollMs = Math.min(nextPollMs, Math.max(50, settleAt - Date.now()));
    }
    if (response.status === 410) throw new CallSessionError("SESSION_EXPIRED");
    await setupDelay(nextPollMs, signal);
  }
  throw new CallSessionError("NO_ANSWER");
}

export async function postCallEvent(credentials: CallCredentials | null, event: string, reason?: string): Promise<void> {
  if (!credentials?.sessionId || !credentials.sessionToken) return;
  await fetch(`${credentials.apiBaseUrl}/call-sessions/${encodeURIComponent(credentials.sessionId)}/events`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${credentials.sessionToken}`,
      "content-type": "application/json"
    },
    body: JSON.stringify({ event, ...(reason ? { reason } : {}) })
  }).catch(() => {});
}

export async function postIcePathEvent(
  credentials: CallCredentials | null,
  telemetry: IcePathTelemetry
): Promise<void> {
  if (!credentials?.sessionId || !credentials.sessionToken) return;
  await fetch(`${credentials.apiBaseUrl}/call-sessions/${encodeURIComponent(credentials.sessionId)}/events`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${credentials.sessionToken}`,
      "content-type": "application/json"
    },
    body: JSON.stringify({ event: "ice-path", ...telemetry })
  }).catch(() => {});
}

export async function endCallSession(credentials: CallCredentials | null): Promise<void> {
  if (!credentials?.sessionId || !credentials.sessionToken) return;
  await fetch(`${credentials.apiBaseUrl}/call-sessions/${encodeURIComponent(credentials.sessionId)}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${credentials.sessionToken}` },
    keepalive: true
  }).catch(() => {});
}
